// Track 2 — Stub-mode checkout return page.
//
// When STRIPE_SECRET_KEY isn't set on the API, the checkout endpoint
// returns this URL instead of a real Stripe Checkout Session URL. We
// read ?plan=… from the query string, POST to /subscriptions/stub-activate,
// then redirect back to /subscription on success. The endpoint 404s
// once Stripe is live, so this page can't be used to skip payment in
// production.

import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { refreshSubscription } from './services/useSubscription';
import { useCurrentUser } from './services/useCurrentUser';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

export default function SubscriptionStubSuccess() {
  const { t } = useTranslation('subscription');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get('plan') || 'pro';
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);
  const currentUser = useCurrentUser();

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // Rapha users shouldn't be able to reach this flow through normal
    // navigation, but a stale tab from before role promotion could land
    // here. Short-circuit before activating any plan on their account.
    if (currentUser.role === 'rapha') {
      navigate('/session', { replace: true });
      return;
    }
    (async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const r = await fetch(`${API}/subscriptions/stub-activate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ planId }),
        });
        if (!r.ok) {
          setError(t('stub.failed'));
          return;
        }
        await refreshSubscription();
        navigate('/subscription', { replace: true });
      } catch {
        setError(t('stub.failed'));
      }
    })();
  }, [planId, navigate, t]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.bg,
        background: colors.gradHero,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 440,
          width: '100%',
          p: 4,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text, mb: 1 }}>
          {t('stub.title')}
        </Typography>
        <Typography sx={{ color: colors.textMuted, mb: 3 }}>{t('stub.body')}</Typography>
        {error ? (
          <Typography sx={{ color: colors.danger }}>{error}</Typography>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: colors.brand }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
